import { roundTo2 } from "utils/formators";
// eslint-disable-next-line import/no-unresolved -- JS util resolved at build time
import { calculateTaxAndTotalCost } from "Hooks/useCalculating/utils";

// The server-authoritative campaign cost (`campaign.detailedCost(cityId)`), shared
// by ADM/EDDM/H2H/D2D — the FE never re-derives the per-mission breakdown. The
// new builder reads it straight from the persisted selection instead of the
// legacy Zustand `useCalculating` engine.
export interface CostLine {
  quantity: number;
  rate: number;
  total: number;
}

export interface ServerDetailedCost {
  missionCostWithoutPrinting: number;
  distribution: CostLine;
  travel: CostLine;
  permit: CostLine;
  printing: CostLine;
  outfit: CostLine;
  subtotal: number;
  tax: number;
  totalCost: number;
  amountOff: number;
  percentOff: number;
}

export interface CostPreviewLine {
  title: string;
  quantity: number;
  price: number;
}

// Mirrors the ADM `campaignCostPreview` shape so the shared drawer renders
// identically across channels.
export interface CampaignCostPreview {
  lines: CostPreviewLine[];
  subtotal: number;
  tax: number;
  total: number;
  amountOff: number;
  percentOff: number;
}

export interface CouponState {
  stripeCouponCode?: string | null;
  amountOff?: number | null;
  percentOff?: number | null;
}

export const EMPTY_COST_PREVIEW: CampaignCostPreview = {
  lines: [],
  subtotal: 0,
  tax: 0,
  total: 0,
  amountOff: 0,
  percentOff: 0,
};

// Composes the drawer's single-line quote from the server breakdown and the
// applied coupon. The quote collapses the per-mission breakdown into one
// channel line (qty = flyers, price = subtotal) per the redesign; tax/total are
// recomputed client-side from the discount so the figure tracks the coupon
// before the campaign record is re-fetched.
export const buildCostPreview = (
  detailedCost: ServerDetailedCost | null | undefined,
  quoteItemLabel: string,
  coupon: CouponState,
  countryTaxRate: number
): CampaignCostPreview => {
  if (!detailedCost) return EMPTY_COST_PREVIEW;

  const subtotal = roundTo2(detailedCost.subtotal ?? 0);
  const flyers = detailedCost.printing?.quantity ?? 0;
  const percentOff = coupon.percentOff ?? 0;
  const amountOff = roundTo2(percentOff ? (percentOff / 100) * subtotal : coupon.amountOff ?? 0);

  const { tax, totalCost } = calculateTaxAndTotalCost({
    countryTaxRate,
    subtotal,
    amountOff: amountOff || null,
  });

  const lines: CostPreviewLine[] = [{ title: quoteItemLabel, quantity: flyers, price: subtotal }];
  if (coupon.stripeCouponCode && amountOff > 0) {
    lines.push({ title: `Coupon ${coupon.stripeCouponCode}`, quantity: 1, price: -amountOff });
  }

  return { lines, subtotal, tax, total: totalCost, amountOff, percentOff };
};
